import { useEffect, useRef, useState } from "react";
import {
  AudioPlaybackHandle,
  pauseOtherAudioPlaybacks,
  registerAudioPlayback,
} from "@components/shared/components/audio-player/audioPlaybackRegistry";

interface UseAudioPlayerResult {
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  togglePlay: () => void;
  seek: (time: number) => void;
}

interface UseAudioPlayerOptions {
  preload?: "none" | "metadata" | "auto";
}

export function useAudioPlayer(src: string, initialDuration = 0, options: UseAudioPlayerOptions = {}): UseAudioPlayerResult {
  const { preload = "metadata" } = options;
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const initialDurationRef = useRef(initialDuration);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(initialDuration);

  initialDurationRef.current = initialDuration;

  useEffect(() => {
    if (!src) return;

    const audio = new Audio();
    audio.preload = preload;
    audio.src = src;
    audioRef.current = audio;

    const handle: AudioPlaybackHandle = { pause: () => audio.pause() };
    const unregister = registerAudioPlayback(handle);

    const onLoadedMetadata = () => setDuration(audio.duration || initialDurationRef.current);
    const onTimeUpdate = () => setCurrentTime(audio.currentTime || 0);
    const onEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
      audio.currentTime = 0;
    };
    const onPause = () => setIsPlaying(false);
    const onPlay = () => {
      setIsPlaying(true);
      pauseOtherAudioPlaybacks(handle);
    };

    audio.addEventListener("loadedmetadata", onLoadedMetadata);
    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("play", onPlay);

    return () => {
      unregister();
      audio.pause();
      audio.removeEventListener("loadedmetadata", onLoadedMetadata);
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("play", onPlay);
      audio.src = "";
      audio.load();
      audioRef.current = null;
      setIsPlaying(false);
      setCurrentTime(0);
    };
  }, [src, preload]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (audio.paused) {
      audio.play().catch((error) => console.error("Audio play failed:", error));
    } else {
      audio.pause();
    }
  };

  const seek = (time: number) => {
    const audio = audioRef.current;
    if (!audio) return;

    const total = audio.duration || duration;
    if (!total) return;

    const clamped = Math.max(0, Math.min(total, time));
    audio.currentTime = clamped;
    setCurrentTime(clamped);
  };

  return { isPlaying, currentTime, duration, togglePlay, seek };
}
